import { injectable } from 'inversify';
import type {
  ITenantRepository,
  TenantGridDTO,
  TenantFormData,
  TenantMembershipDTO,
  TenantMembershipsResult,
  UpdateClinicLoginPasswordResult,
  CreateTenantResult,
} from '@/domain/repositories/ITenantRepository';
import { Tenant } from '@/domain/entities/Tenant';

@injectable()
export class MockTenantRepository implements ITenantRepository {
  private mockTenants: TenantGridDTO[] = [
    {
      id: 1,
      nombre: 'Clínica San José',
      emailAdministrador: 'admin@clinicasanjose.com',
      activo: true,
      fechaActivoUTC: new Date().toISOString(),
      fechaVencimiento: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      montoPeriodoPago: 18500,
      planAdminId: 1,
      estadoPagoId: 1,
      estadoPagoDescripcion: 'Abonado',
      fechaPeriodoPago: '2026-01',
    },
    {
      id: 2,
      nombre: 'Clínica Central',
      emailAdministrador: 'admin@clinicacentral.com',
      activo: true,
      fechaActivoUTC: new Date().toISOString(),
      fechaVencimiento: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString(),
      montoPeriodoPago: 12000,
      planAdminId: 3,
      estadoPagoId: 2,
      estadoPagoDescripcion: 'Mora',
      fechaPeriodoPago: '2025-12',
    },
    {
      id: 3,
      nombre: 'Clínica Norte',
      emailAdministrador: 'admin@clinicanorte.com',
      activo: false,
      fechaActivoUTC: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
      fechaVencimiento: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      montoPeriodoPago: 9500,
      planAdminId: 1,
      estadoPagoId: 3,
      estadoPagoDescripcion: 'Impago',
    },
  ];

  async getAll(): Promise<TenantGridDTO[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...this.mockTenants];
  }

  async getById(id: number): Promise<Tenant> {
    await new Promise(resolve => setTimeout(resolve, 200));
    const tenantData = this.mockTenants.find(t => t.id === id);
    
    if (!tenantData) {
      throw new Error('Tenant not found');
    }

    return Tenant.create(
      tenantData.id,
      tenantData.nombre,
      tenantData.emailAdministrador,
      'default_db',
      'default_connection',
      'America/Argentina/Buenos_Aires',
      new Date(tenantData.fechaActivoUTC),
      tenantData.activo,
      tenantData.fechaVencimiento ? new Date(tenantData.fechaVencimiento) : undefined,
      undefined,
      tenantData.planAdminId
    );
  }

  async create(data: TenantFormData): Promise<CreateTenantResult> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const newId = Math.max(...this.mockTenants.map(t => Number(t.id))) + 1;
    const newTenant: TenantGridDTO = {
      id: newId,
      nombre: data.nombre,
      emailAdministrador: data.emailAdministrador,
      activo: true,
      fechaActivoUTC: new Date().toISOString(),
      fechaVencimiento: data.fechaVencimiento?.toISOString(),
      planAdminId: data.planAdminId,
    };

    this.mockTenants.push(newTenant);

    return {
      tenant: Tenant.create(
        newId,
        data.nombre,
        data.emailAdministrador,
        data.baseDeDatos || 'default_db',
        data.connectionString || 'default_connection',
        data.timeZoneId,
        new Date(),
        true,
        data.fechaVencimiento,
        undefined,
        data.planAdminId
      ),
      credentialsEmailSent: false,
      linkedExistingAccount: false,
    };
  }

  async update(id: number, data: TenantFormData): Promise<Tenant> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const index = this.mockTenants.findIndex(t => t.id === id);
    if (index === -1) {
      throw new Error('Tenant not found');
    }

    this.mockTenants[index] = {
      ...this.mockTenants[index],
      nombre: data.nombre,
      emailAdministrador: data.emailAdministrador,
      fechaVencimiento: data.fechaVencimiento?.toISOString(),
      planAdminId: data.planAdminId,
    };

    return Tenant.create(
      id,
      data.nombre,
      data.emailAdministrador,
      data.baseDeDatos || 'default_db',
      data.connectionString || 'default_connection',
      data.timeZoneId,
      new Date(this.mockTenants[index].fechaActivoUTC),
      this.mockTenants[index].activo,
      data.fechaVencimiento,
      undefined,
      data.planAdminId
    );
  }

  async delete(id: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const index = this.mockTenants.findIndex(t => t.id === id);
    if (index !== -1) {
      this.mockTenants.splice(index, 1);
    }
  }

  async changeStatus(id: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const tenant = this.mockTenants.find(t => t.id === id);
    if (tenant) {
      tenant.activo = !tenant.activo;
    }
  }

  async getMyMemberships(email?: string): Promise<TenantMembershipsResult> {
    return {
      email: email || 'soporte@coofit.life',
      memberships: [],
      hasCredentials: true,
      credentialsLocked: false,
    };
  }

  async addMyMembership(tenantId: string | number, email?: string): Promise<TenantMembershipDTO> {
    const tenant = this.mockTenants.find((item) => String(item.id) === String(tenantId));
    return {
      id: String(tenantId),
      tenantId: String(tenantId),
      tenantName: tenant?.nombre ?? `Clínica ${tenantId}`,
      tenantEmailAdministrador: tenant?.emailAdministrador ?? '',
      tenantActivo: tenant?.activo ?? true,
      email: email || 'soporte@coofit.life',
      isOwner: true,
      activo: true,
      creadoEn: new Date().toISOString(),
    };
  }

  async changeMembershipsEmail(
    fromEmail: string,
    toEmail: string
  ): Promise<TenantMembershipsResult> {
    return {
      email: toEmail || fromEmail,
      memberships: [],
      hasCredentials: true,
      credentialsLocked: false,
    };
  }

  async updateClinicLoginPassword(
    email: string,
    _password: string,
    sendEmail?: boolean
  ): Promise<UpdateClinicLoginPasswordResult> {
    return {
      email,
      created: false,
      emailSent: Boolean(sendEmail),
      hasCredentials: true,
      credentialsLocked: false,
    };
  }

  async deleteMyMembership(_id: string, _email?: string): Promise<void> {
    return;
  }

  async updateMyMembership(
    id: string,
    patch: { tenantId?: string | number; activo?: boolean },
    email?: string
  ): Promise<TenantMembershipDTO> {
    const tenant = this.mockTenants.find((item) => String(item.id) === String(patch.tenantId ?? id));
    return {
      id,
      tenantId: String(patch.tenantId ?? id),
      tenantName: tenant?.nombre ?? `Clínica ${patch.tenantId ?? id}`,
      tenantEmailAdministrador: tenant?.emailAdministrador ?? '',
      tenantActivo: tenant?.activo ?? true,
      email: email || 'soporte@coofit.life',
      isOwner: true,
      activo: patch.activo ?? true,
      creadoEn: new Date().toISOString(),
    };
  }
}
